import NiceModal, { useModal } from '@ebay/nice-modal-react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  type ComponentProps,
  type FC,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { Z_INDEXES } from '../constants/styles';

type ModalProps = PropsWithChildren;

// react-select renders its menu into a portal on document.body (see menuPortalTarget
// in shared/ui/select.tsx), outside the Dialog.Content DOM subtree. Radix treats
// clicks there as "outside" the dialog and closes it — ignore those clicks instead.
const isReactSelectPortalEvent = (event: Event) =>
  event.target instanceof Element &&
  event.target.closest('.react-select__menu-portal') !== null;

const BaseModal: FC<ModalProps> = ({ children }) => {
  const { visible, show, hide } = useModal();

  return (
    <Dialog.Root
      open={visible}
      onOpenChange={(open) => (open ? show() : hide())}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="data-[state=open]:animate-overlay-show fixed inset-0 bg-white/70 backdrop-blur-sm"
          style={{ zIndex: Z_INDEXES.OVERLAY }}
        />
        <VisuallyHidden>
          <Dialog.Title />
        </VisuallyHidden>

        <Dialog.Content
          className="bg-neutral-1 border-neutral-5 fixed top-1/2 left-1/2 max-h-[85vh] w-fit max-w-[70vw] -translate-x-1/2 -translate-y-1/2 border px-8 pt-8 pb-6 shadow focus:outline-none"
          style={{ zIndex: Z_INDEXES.MODAL }}
          onInteractOutside={(event) => {
            if (isReactSelectPortalEvent(event)) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (isReactSelectPortalEvent(event)) {
              event.preventDefault();
            }
          }}
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const registerModal = <T extends FunctionComponent<any>>(
  Component: T
) => {
  return NiceModal.create((props: ComponentProps<T>) => (
    <BaseModal>
      <Component {...props} />
    </BaseModal>
  ));
};

export const DialogTitle = Dialog.Title;
